import { Injectable, inject, Injector } from "@angular/core";
import { MatSnackBar } from "@angular/material/snack-bar";
import { ActivatedRoute, Router } from "@angular/router";
import { FuseAlertType } from "@fuse/components/alert";
import "quill-mention";
import { Token } from "app/models/Auth.model";
import { environment } from "environments/environment";
import { FileUploader, FileItem, FileUploaderOptions, FileLikeObject } from 'ng2-file-upload';
import { Account, UserImpl } from "./user";
import { startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfQuarter, endOfQuarter } from 'date-fns';
import { ASSISTANT_ICON_MAP, CUSTOM_THEME_ELEMENTS_CLASSES, DateRangeEnum } from "../constants";
import { iconTypes } from 'app/modules/admin/apps/file-manager/file-manager.constants';
import { ActivityDes, Asset, AssetPathItem, FileUploadOptions, activity } from "./document";
import { QuillModules } from "ngx-quill";
import { MatDialog } from "@angular/material/dialog";
import { SortDirection } from "@angular/material/sort";
import { DMSViews, SortingPreference, pickerDirectory, OversizedFile } from "app/modules/admin/apps/file-manager/file-manager.types";
import { marked } from 'marked';
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { LpmConfig, LpmScope, LpmCopilotConfig } from "./lpm.config";
import { DmsClientTheme } from "./dms-client-theme";
import { DMS_MAX_FILE_SIZE_BYTES } from "app/shared/global-constants";
import { FuseConfirmationService } from "@fuse/services/confirmation";
import * as consts from 'app/shared/global-constants';
import { ToastrService } from "ngx-toastr";
import { DmsFileUploader } from "./dms-file-item";
import { LpmLabelPipe } from "@fuse/pipes/lpm-label.pipe";
import { CopilotAssetDetails } from "./copilot.model";
import { AuthService } from "app/services/auth.service";
import { ActionRegistryService } from "app/shared/action-registry.service";
import { DesktopAgentService } from "app/services/desktop-agent.service";
import { PermissionsService } from "app/modules/admin/apps/file-manager/permissions/permissions.service";
import { FileManagerService } from "app/modules/admin/apps/file-manager/file-manager.service";
import { WebComponentInputParams } from "./web-component-input-params.model";

@Injectable({
	providedIn: "root",
})
export class Util {
	private USER_TOKEN = "USER_TOKEN";
	private USER_ID = "USER_ID";
	private DMS_LPM = "DMS_LPM";
	private USER_IN_SESSION = "DMS_USER_IN_SESSION";
	private ACCOUNT_IN_SESSION = "ACCOUNT_IN_SESSION";
	private ROOT_IN_SESSION = "ROOT_IN_SESSION";
	private SORT_PREFERNCES = "SORT_PREFERNCES";
	public USER_TYPE:LpmScope = "LPM";
	DateRanges = DateRangeEnum;
	iconTypes: any = iconTypes;
	fileUploader: DmsFileUploader;
	WebComponentInputParams: WebComponentInputParams = new WebComponentInputParams();
	private webComponentInputParamsByInstance = new Map<string, WebComponentInputParams>();

	private mentionedUsers = [];
	private supportedFileTypesForEdit: Set<string> = new Set([
		'doc', 'docx', 'docm', 'dot', 'dotm', 'dotx', 'odt', 'ott', 'rtf', 
		'xls','xlt','xlsx','xlsm','xltx', 'xltm','xlsb','scx', 'ods', 'fods','dif', 'dsv','csv', 'tsv',
		'ppt','pptx','pptm','pps','ppsx','ppsm','pot','potx','potm','odp','otp',
		'txt','html','xml']);
    public allowedFile = "image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation, application/json, text/plain,video/*,audio/*"
	constructor(
		private router: Router,
		private actvRoute: ActivatedRoute,
		public snack: MatSnackBar,
		private _matDialog: MatDialog,
		private sanitizer: DomSanitizer,
		private _fuseConfirmationService: FuseConfirmationService,
		private toastr: ToastrService,
		private _injector: Injector,
	) { }

	private get authService(): AuthService {
		return this._injector.get(AuthService);
	}

	/**
	 * Sets the user token in the local storage.
	 * @param token - The token to be stored.
	 */
	public setToken(token: Token) {
		localStorage.setItem(this.USER_TOKEN, JSON.stringify(token));
	}

	/**
	 * Retrieves the user token from the session.
	 * @returns The user token.
	 */
	public getToken() {
		return this.getItemFromSession(this.USER_TOKEN);
	}
	public setUserInSession(user: UserImpl) {
		localStorage.setItem(this.USER_IN_SESSION, JSON.stringify(user));
	}

	public setUserId(userId : string) {
		localStorage.setItem(this.USER_ID, JSON.stringify(userId));
	}
	
	public setAccountInSession(account: Account) {
		localStorage.setItem(this.ACCOUNT_IN_SESSION, JSON.stringify(account));
	}

	public setRootInSession(rootID:string) {
		localStorage.setItem(this.ROOT_IN_SESSION, JSON.stringify(rootID));
	}

	public getUserFromSession() {
		return this.getItemFromSession(this.USER_IN_SESSION);
	}

	public getAccountFromSession() {
		return this.getItemFromSession(this.ACCOUNT_IN_SESSION);
	}

	public getRootFromSession() {
		// avoid cross-tab localStorage leak. still keep the fallback to session storage for standalone application usage.
		return this.WebComponentInputParams?.folderId ?? this.getItemFromSession(this.ROOT_IN_SESSION);
	}


	/**
	 * Trims an ancestor path array (root-to-leaf) so it starts at the current scoping root
	 * (see getScopedRootId), preventing scoped/embedded views from displaying or linking to
	 * folders above it.
	 */
	public scopePathToRoot<T extends AssetPathItem>(paths: T[]): T[] {
		const webComParams = this.WebComponentInputParams;
		const rootId = webComParams?.folderId ?? this.getRootFromSession();
		if (rootId) {
			const rootIndex = paths.findIndex((path) => path.id === rootId);
			if (rootIndex > 0) {
					paths.splice(0, rootIndex);
			} else if (rootIndex === -1) {
				// Current folder isn't a descendant of the configured root
				    paths = paths.slice(-1);
			}
		}
		paths = paths.filter(path => path.access || path.name === 'My Drive');
		return paths;
	}

	public getUserId() {
		// avoid cross-tab localStorage leak. still keep the fallback to session storage for standalone application usage.
		return this.WebComponentInputParams?.userId ?? this.getItemFromSession(this.USER_ID)
	}


	public getWebComponentInputParams(instanceId?: string): WebComponentInputParams {
		if (instanceId && this.webComponentInputParamsByInstance.has(instanceId)) {
			return this.webComponentInputParamsByInstance.get(instanceId);
		}
		return this.WebComponentInputParams;
	}

	public setUserTwoFactorInSession() {
		let user: UserImpl = this.getUserFromSession();
		localStorage.setItem(`${user.id}_TWO_FACTOR_AUTH`, JSON.stringify(true));
	}

	public getUserTwoFactorFromSession(key) {
		return this.getItemFromSession(`${key}_TWO_FACTOR_AUTH`);
	}

	/** Permanently dismisses the desktop agent install banner for the current user on this device. */
	public dismissDesktopAgentBanner() {
		const userId = this.getUserId();
		localStorage.setItem(`DESKTOP_AGENT_BANNER_DISMISSED_${userId}`, JSON.stringify(true));
	}

	public isDesktopAgentBannerDismissed(): boolean {
		const userId = this.getUserId();
		return !!this.getItemFromSession(`DESKTOP_AGENT_BANNER_DISMISSED_${userId}`);
	}

	public setIsWebApp(value) {
		localStorage.setItem(`WEB_APP`, JSON.stringify(value));
	}

	public getIsWebApp() {
		return this.getItemFromSession(`WEB_APP`);
		//return true;
	}
	
	public setMentionedUsers(users) {
		this.mentionedUsers = users;
	}

	/**
	 * Retrieves the page limit from the local storage.
	 * If the page limit is not found in the local storage, a default value of 20 is returned.
	 * @returns The page limit value.
	 */
	public getPageLimit() {
		const pageLimitFromSession = localStorage.getItem("pageLimit");
		if (pageLimitFromSession != undefined && pageLimitFromSession != null && consts.pagelimits.includes(Number(pageLimitFromSession))) {
			return pageLimitFromSession;
		}

		return 100;
	}

	/**
	 * Retrieves the page limit for large pages.
	 * If the page limit is stored in the local storage, it will be returned.
	 * Otherwise, a default value of 25 will be returned.
	 * 
	 * @returns The page limit for large pages.
	 */
	public getPageLimitLarge() {
		let pageLimitFromSession = localStorage.getItem("pageLimitLarge");
		if (pageLimitFromSession != undefined && pageLimitFromSession != null) return pageLimitFromSession;

		return 25;
	}

	/**
	 * Sets the page limit for the application.
	 * @param {number} pageLimit - The desired page limit.
	 */
	public setPageLimit(pageLimit) {
		localStorage.setItem("pageLimit", pageLimit);
	}

	/**
	 * Sets the page limit for large pages.
	 * @param {number} pageLimit - The page limit to be set.
	 */
	public setPageLimitLarge(pageLimit) {
		localStorage.setItem("pageLimitLarge", pageLimit);
	}

	/**
	 * Navigates to the dashboard by using the router.
	 */
	public navigateToDashboard() {
		this.router.navigateByUrl("/apps/file-manager");
	}

	navigateToLogin(){
		this.router.navigateByUrl("/apps/auth/login");
	}

	/**
	 * Retrieves an item from the session storage.
	 * @param item - The name of the item to retrieve.
	 * @returns The retrieved item, or null if it doesn't exist.
	 */
	public getItemFromSession(item: string) {
		let data = localStorage.getItem(item);

		if (data == undefined || data == null || data == 'undefined') {
			// this.router.navigateByUrl('');
			// alert('Your Session expire Please login');
			// this.clearLocalStorage();
			return null;
		}

		return JSON.parse(data);
	}

	public dismissAllModals() {
		this._matDialog.closeAll();
	}

	/**
	 * Creates an alert object with the specified type and message.
	 * 
	 * @param type - The type of the alert.
	 * @param message - The message of the alert.
	 * @returns An alert object with the specified type and message.
	 */
	getAlert(type: FuseAlertType, message: string) {
		return { type: type, message: message }
	}

	/**
	 * Returns the toolbar options for the editor.
	 * @param links - Whether to include the link option in the toolbar.
	 * @returns The toolbar options.
	 */
	public static getEditorToolbarOptions(links?) {
		//for passing showAlert scope
		let _this = this;
		const toolbarOption: QuillModules = {
			toolbar: [
				["bold", "italic", "underline", "strike"], // toggled buttons
				[{ list: "ordered" }, { list: "bullet" }],
				//[{ font: [] }],
				[{ align: [] }],
				["blockquote", "code-block"],

				//[{ header: 1 }, { header: 2 }], // custom button values

				//[{ script: "sub" }, { script: "super" }], // superscript/subscript
				[{ indent: "-1" }, { indent: "+1" }], // outdent/indent
				//[{ direction: "ltr" }], // text direction

				//[{ size: ["small", false, "large", "huge"] }], // custom dropdown
				[{ header: [1, 2, 3, 4, 5, 6, false] }],

				["clean"], // remove formatting button
				//["code-block"],
				//[files ? "attachFiles" : false],
				[links ? "link" : 'false'],
				//['link', 'image', 'video']                         // link and image, video
			],
		};

		return toolbarOption;
	}

	/**
	 * Displays a toastr message.
	 * @param message - The message to display.
	 * @param title - The optional title of the toastr message.
	 */
	setToastr(message, title?) {
		this.snack.open(message, title ? title : '', {
			duration: 3000,
			horizontalPosition: 'start',
			panelClass: ['custom-snackbar']
		});
	}

	setUserType(type: LpmScope) {
		this.USER_TYPE = type;
	}

	setWebComponentInputParams(inputParams: WebComponentInputParams, instanceId?: string) {
		const HOST_DEFAULTS: Record<string, Partial<LpmConfig>> = {
			'PracticePanther': {
				isInternalShareSupported: false,
				isExternalShareSupported: true,
				viewPermissions: false,
				isUserTaggingSupported: false,
				deleteLabelText: 'Delete',
				trashLocationLabel: 'Recycle Bin',
				shareModalTitle: 'External Sharing',
				showTrashPageTitle: false,
				emptyTrashButtonWithLabel: true,
				downloadConfirmButtonIcon: true,
				allowOpenOnDesktop: false,
				isFileNameRequiredInGlobalSearch: false,
				externalShareSelectLabel: 'Select Contacts',
				externalShareInstructions: 'Select contacts and manage access levels',
				externalShareWriterRoleLabel: 'Writer',
				externalShareViewerRoleLabel: 'Viewer',
				fixedExternalList: true,
			},
			'LollyLaw': {
				// Base configuration defaults handled in LpmConfig init
			},

		};

		// keyed by host:scope, applied after host defaults
		const HOST_SCOPE_DEFAULTS: Record<string, Partial<LpmConfig>> = {
			// PP client portal users
			'PracticePanther:EXT': {
				allowShare: false,
				allowLock: false,
				allowRename: false,
				allowMove: false,
				allowTrash: false,
				organizeFavorite: false,
				organizeLabel: false,
				viewInfoDetails: false,
				viewActivityDetails: false,
				viewNotesDetails: false,
				filterByLabel: false,
				filterByTrash: false,
				filterByFavorite: false,
			},
			'LollyLaw:EXT': {
				allowOpenOnDesktop: false,
			},
			'ALL:EXT': {
				allowOpenOnDesktop:false
			}

		};

		const HOST_THEME_DEFAULTS: Record<string, Partial<DmsClientTheme>> = {
			'PracticePanther': {
				name: 'panther_default',
				fontFamily:"Poppins",
				buttonBorderRadius: '4px',
			},
		};

		if (inputParams) {
			// set undefined values to defaults based on the host
			const incoming = inputParams.lpmConfig ?? new LpmConfig();
			const merged = new LpmConfig();
			this.mergeDefined(merged, HOST_DEFAULTS[incoming.host] ?? {});
			const scope = incoming.scope ?? merged.scope;
			if(incoming.host){
				this.mergeDefined(merged, HOST_SCOPE_DEFAULTS[`${incoming.host}:${scope}`] ?? {});
			}else{
				this.mergeDefined(merged, HOST_SCOPE_DEFAULTS[`ALL:${scope}`] ?? {}); // fallback to LollyLaw defaults if host is not provided
			}
			this.mergeDefined(merged, incoming);
			merged.copilotConfig = this.mergeDefined(new LpmCopilotConfig(), incoming.copilotConfig ?? {});
			inputParams.lpmConfig = merged;

			// host theme defaults, input fields win
			const theme = new DmsClientTheme();
			this.mergeDefined(theme, HOST_THEME_DEFAULTS[merged.host] ?? {});
			this.mergeDefined(theme, inputParams.clientTheme ?? {});
			inputParams.clientTheme = theme;
		}
		this.WebComponentInputParams = inputParams;
		if (instanceId) {
			this.webComponentInputParamsByInstance.set(instanceId, inputParams);
		}
	}

	/**
	 * Drops the per-instance copy of WebComponentInputParams for a destroyed
	 * <layout-web> instance so its state doesn't linger in memory.
	 */
	clearWebComponentInputParams(instanceId: string) {
		if (instanceId) {
			this.webComponentInputParamsByInstance.delete(instanceId);
		}
	}

	/**
	 * Copies only the defined own properties of `source` onto `target`, leaving
	 * `target`'s existing (default) values in place for anything `source` omits.
	 */
	private mergeDefined<T extends object>(target: T, source: Partial<T>): T {
		for (const key in source) {
			if (source[key] !== undefined && source[key]!== null) {
				target[key] = source[key];
			}
		}
		return target;
	}

	/**
	 * Creates a FileUploader instance for uploading files.
	 * 
	 * @param folderId - The ID of the folder to upload the files to. If not provided, files will be uploaded to the root folder.
	 * @returns A FileUploader instance configured with the appropriate URL and headers.
	 */
	public fileUpload(options: FileUploadOptions) {
		const {
			folderId = null,
			replace = false,
			matterId = null,
			contactId = null,
		  } = options;
		
		let uploader: DmsFileUploader;
		let url = this.getFileUploadUrl(folderId, replace);
		const queryParams = this.getQueryMethod({
			replace: replace,
			matterId: matterId,
			contactId: contactId
		});
		if (folderId){
			url = `${url}/${folderId}${queryParams}`
		}
		const headers = [{ 'name': 'USER_TYPE', 'value': !this.getIsWebApp() ? 'DMS' : this.USER_TYPE}]
		const allowedFileType = ['image', 'pdf', 'doc', 'xls', 'ppt', 'audio', 'video', 'json', 'text', 'compress','csv','application/json'];
		const allowedExtensions = ['heic', 'heif','json','csv'];
	
		uploader = new DmsFileUploader({ url: url, headers, maxFileSize: consts.DMS_MAX_FILE_SIZE_BYTES, allowedFileType: allowedFileType });
	
		// Customise the fileType filter to allow HEIC/HEIF by extension as borwser does not recognize any MIME type for HEIC/HEIF files.
		const fileTypeFilter = uploader.options.filters.find(f => f.name === 'fileType');
		if (fileTypeFilter) {
			const originalFn = fileTypeFilter.fn.bind(uploader);
			fileTypeFilter.fn = (item: FileLikeObject) => {
				const ext = item.name?.split('.').pop()?.toLowerCase();
				if (allowedExtensions.includes(ext)) {
					return true;
				}
				return originalFn(item);
			};
		}
	
		uploader.onBeforeUploadItem = (item: FileItem) => {
			item.withCredentials = true
		};
	
		uploader.onWhenAddingFileFailed = (item: FileLikeObject, filter: any, options: any) => {
			let error_message = '';
			switch (filter.name) {
				case 'fileSize':
					const maxSizeMB = this.formatBytes(consts.DMS_MAX_FILE_SIZE_BYTES);
					error_message = `File "${item.name}" exceeds the maximum allowed size of ${maxSizeMB}.`;
					break;
				case 'fileType':
					if (item.size === 0 && !item.type) return; // folder dropped via drag-and-drop
					const allowedTypes = allowedFileType
						.map(type => type.charAt(0).toUpperCase() + type.slice(1))
						.join(' • ');
					error_message = `File "${item.name}" has an unsupported file type.\nAllowed types: ${allowedTypes}`;
					break;
				default:
					error_message = `File "${item.name}" could not be added. Reason: ${filter.name}`;
					break;
			}

			this.toastr.error(error_message, 'Upload Error', {
				disableTimeOut: true,
				closeButton: true
			});

		};
		this.fileUploader =  uploader;
		return uploader
	}

	/**
	 * Creates a FileUploader instance for uploading files.
	 * 
	 * @param sessionId - The ID of the session to upload the files to.
	 * @returns A FileUploader instance configured with the appropriate URL and headers.
	 */
		public fileUploadToRag(sessionId: string = null) {
			let uploader: FileUploader
			const maxSizeBytes = 100 * 1024 * 1024; // 100MB in bytes
			const url = `${environment.RAG_FILE_UPLOAD_URL}/${sessionId}/embeddings/upload` 
			
			const headers = [{ 'name': 'USER_TYPE', 'value': this.getIsWebApp() ? this.USER_TYPE : 'DMS'}]
			uploader = new FileUploader({ url, headers ,maxFileSize:maxSizeBytes});
			uploader.onBeforeUploadItem = (item: FileItem) => {
				item.withCredentials = true
			};
			return uploader
		}

	/**
	 * Helper to get the correct upload URL without creating an uploader instance.
	 */
	public getFileUploadUrl(folderId: string = null, replace: boolean = false): string {
		let url = environment.ROOT_FILE_UPLOAD_URL;
		if (folderId) {
			url = `${url}/${folderId}?replace=${replace}`;
		}
		return url;
	}

	/**
	 * Removes HTML tags and replaces "&nbsp;" with a space from the given content.
	 * @param {string} content - The content to remove HTML tags from.
	 * @returns {string} The content without HTML tags and with "&nbsp;" replaced by a space.
	 */
	public removeHtmlTags(content) {
		return content ? content.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ") : "";
	}

	/**
	 * Returns the CSS class color based on the file type.
	 * @param filetype - The file type.
	 * @returns The CSS class color.
	 */
	public getTypeColor(filetype: string): string {
		let classColor = 'bg-dark';
		if (filetype.includes(".")) {
			filetype = filetype.replace(/\./g, ''); 
		}
		switch (filetype) {
			case 'pdf':
				classColor = 'bg-red-600';
				break;
			case 'doc':
			case 'docx':
			case 'pages':
				classColor = 'bg-blue-600';
				break;
			case 'xls':
			case 'xlsx':
			case 'csv':
			case 'numbers':
				classColor = 'bg-green-600';
				break;
			case 'ppt':
			case 'pptx':
			case 'key':
				classColor = 'bg-amber-600';
				break;
			case 'jpg':
			case 'jpeg':
			case 'png':
			case 'gif':
				classColor = 'bg-purple-600';
				break;
			case 'mp3':
			case 'm4a':
			case 'wav':
				classColor = 'bg-teal-600';
				break;
			case 'mp4':
			case 'avi':
			case 'mov':
				classColor = 'bg-orange-600';
				break;
			case 'zip':
			case 'rar':
			case '7z':
				classColor = 'bg-yellow-600';
				break;
			default:
				classColor = 'bg-gray-600';
				break;
		}
		return classColor;
	}

	public isSupportedFileTypeForEdit(asset: Asset): boolean {
		if (!asset){
			return false;
		}
		const filetype = this.removePunctuation(asset.extension?.toLowerCase())??'docx';
		return this.supportedFileTypesForEdit.has(filetype.toLowerCase());
	}

	public removePunctuation(value: string): string {
		return value?.replace(/[.,\/#!$%\^&\*;:{}=\-_`~()]/g, '')??"";
	}
	/**
	 * Returns the progress of a file upload.
	 * @param item - The file item to get the progress for.
	 * @returns The progress value, ranging from 0 to 100.
	 */
	public getProgress(item: FileItem): number {
		if (item.isUploading) {
			return item.progress===0 ? item.progress : (item.progress-1);
		} else if (item.isSuccess) {
			return 100;
		} else if (item.isError) {
			return 0;
		} else if (item.isCancel) {
			return 0;
		} else {
			return 0;
		}
	}

	/**
	 * Returns the color of the icon based on the state of the file item.
	 * @param item The file item to determine the icon color for.
	 * @returns The color class name for the icon.
	 */
	public getIconColor(item: FileItem): string {
		if (item.isError) {
			return 'icon-color-danger';
		} else
			if (item.isUploading) {
				return 'icon-color-info';
			} else if (item.isUploaded && item.isSuccess) {
				return 'icon-color-success';
			} else if (item.isCancel) {
				return 'icon-color-dark';
			} else {
				return 'icon-color-dark';
			}
	}

	/**
	 * Returns the tooltip text based on the status of the file item.
	 * @param item - The file item.
	 * @returns The tooltip text.
	 */
	public getStatusTooltip(item: FileItem,errorMessage?:string): string {
		if (item.isError) {
			return errorMessage?errorMessage:'An error has occurred'; //TODO - add error message from server into item object
		} else
			if (item.isUploading) {
				return 'uploading';
			} else if (item.isSuccess) {
				return 'done';
			} else if (item.isCancel) {
				return 'canceled';
			} else {
				return 'ready';
			}
	}

	/**
	 * Returns the icon name based on the state of the file item.
	 * @param item - The file item.
	 * @returns The icon name.
	 */
	public getIcon(item: FileItem): string {
		if (item.isError) {
			return 'feather:alert-triangle';
		} else
			if (item.isUploading) {
				return 'feather:upload-cloud';
			} else if (item.isSuccess) {
				return 'feather:check-circle';
			} else if (item.isCancel) {
				return 'feather:circle';
			} else {
				return 'feather:circle';
			}
	}

	/**
	 * Represents the configuration for the cancel form.
	 */
	public static cancelForm = {
		"title": "Cancel",
		"message": "You are about to cancel the upload process. </br></br><span class=\"font-medium\">Are you sure?</span>",
		"icon": {
			"show": true,
			"name": "mat_outline:cancel_schedule_send",
			"color": "warning"
		},
		"actions": {
			"confirm": {
				"show": true,
				"label": "Stop Process",
				"color": "warn"
			},
			"cancel": {
				"show": true,
				"label": "Nevermind"
			}
		},
		"dismissible": true
	}

	/**
	 * Represents the configuration for the trash form.
	 */
	get trashForm () {
		const trashConfirmButtonColor = this.getConfirmElementsColor("TrashConfirmButton");
		const label = new LpmLabelPipe(this).transform('Trash', 'deleteLabelText');
		const trashLocation = new LpmLabelPipe(this).transform('Trash', 'trashLocationLabel');
		return{
		"title": label,
		"message": `Are you sure you want to delete this item? </br></br><span class="font-medium">This item will be moved to ${trashLocation}!</span>`,
		"icon": {
			"show": true,
			"name": "mat_outline:delete",
			"color": "error"
		},
		"actions": {
			"confirm": {
				"show": true,
				"label": label,
				"color": trashConfirmButtonColor ?? 'warn'
			},
			"cancel": {
				"show": true,
				"label": "Cancel"
			}
		},
		"dismissible": true
		}
	}

	/**
	 * Represents the configuration for the trash form.
	 */
	public static CBForm = {
		"title": "Information",
		"message": "<span class=\"text-primary\">Client Bridge</span> is not active for this user.</br><span class=\"font-medium\">Please enable bridge access and try again. </span>",
		"icon": {
			"show": true,
			"name": "heroicons_outline:exclamation-triangle",
			"color": "warning"
		},
		"actions": {
			"confirm": {
				"show": true,
				"label": "OK",
				"color": "accent"
			},
			"cancel": {
				"show": false,
				"label": "Cancel"
			}
		},
		"dismissible": true
	}

	get trashMultiFiles() {
		const label = new LpmLabelPipe(this).transform('Trash', 'deleteLabelText');
		const trashLocation = new LpmLabelPipe(this).transform('Trash', 'trashLocationLabel');
		return {
		"title": label,
		"message": `Are you sure you want to delete these items? </br></br><span class="font-medium">These items will be moved to ${trashLocation}!</span>`,
		"icon": {
			"show": true,
			"name": "mat_outline:delete",
			"color": "error"
		},
		"actions": {
			"confirm": {
				"show": true,
				"label": label,
				"color": "warn"
			},
			"cancel": {
				"show": true,
				"label": "Cancel"
			}
		},
		"dismissible": true
		};
	}

	/**
	 * Represents the configuration for the delete form.
	 */
	get deleteForm() {
		const trashLocation = new LpmLabelPipe(this).transform('Trash', 'trashLocationLabel');
		return {
		"title": "Delete",
		"message": `<span class="font-medium">This item will be removed from ${trashLocation} and deleted permanently!</span></br></br>Are you sure? `,
		"icon": {
			"show": true,
			"name": "mat_outline:delete",
			"color": "error"
		},
		"actions": {
			"confirm": {
				"show": true,
				"label": "Delete",
				"color": "warn"
			},
			"cancel": {
				"show": true,
				"label": "Cancel"
			}
		},
		"dismissible": true
		};
	}

	get deleteMultiForm() {
		const trashLocation = new LpmLabelPipe(this).transform('Trash', 'trashLocationLabel');
		return {
		"title": "Delete",
		"message": `<span class="font-medium">You are about to remove multiple items from ${trashLocation} and delete them permanently!</span></br></br>Are you sure? `,
		"icon": {
			"show": true,
			"name": "mat_outline:delete",
			"color": "error"
		},
		"actions": {
			"confirm": {
				"show": true,
				"label": "Delete",
				"color": "warn"
			},
			"cancel": {
				"show": true,
				"label": "Cancel"
			}
		},
		"dismissible": true
		};
	}

	/**
	 * Represents the configuration for the delete all selected files form.
	 */
	get deleteAllForm() {
		const trashLocation = new LpmLabelPipe(this).transform('Trash', 'trashLocationLabel');
		return {
		"title": "Delete",
		"message": `<span class="font-medium">This action will remove all items in ${trashLocation}. All items will be permanently deleted!</span></br></br>Are you sure? `,
		"icon": {
			"show": true,
			"name": "mat_outline:delete",
			"color": "error"
		},
		"actions": {
			"confirm": {
				"show": true,
				"label": "Delete",
				"color": "warn"
			},
			"cancel": {
				"show": true,
				"label": "Cancel"
			}
		},
		"dismissible": true
		};
	}

	/**
	 * Represents the configuration for the download file form.
	 */
	get downloadForm () {
		const confirm_download_button_color = this.getConfirmElementsColor("Download");
		const dialogIconColor = this.getConfirmElementsColor("dialogIcon");
		return {
		"title": "Download",
		"message": "You are about to download multiple items. This may take a moment as the items will be compressed prior to download. <br><br> <strong>Proceed with download</strong>?",
		"icon": {
			"show": true,
			"name": "feather:download",
			"color": dialogIconColor ?? 'info'
		},
		"actions": {
			"confirm": {
				"show": true,
				"label": "Download",
				"color": confirm_download_button_color ?? 'accent',
				"icon": {
					"show": true,
					"name": "feather:download",
					"flag": "downloadConfirmButtonIcon"
				}
			},
			"cancel": {
				"show": true,
				"label": "Cancel"
			}
		},
		"dismissible": true
		}
	}

	get singledownloadForm () {
		const confirm_download_button_color = this.getConfirmElementsColor("Download");
		const dialogIconColor = this.getConfirmElementsColor("dialogIcon");
		return {
		"title": "Download",
		"message": "You are about to download a file. This may take a moment to prepare. <br><br> <strong> Proceed with download </strong> ?",
		"icon": {
			"show": true,
			"name": "feather:download",
			"color": dialogIconColor ?? "info"
		},
		"actions": {
			"confirm": {
				"show": true,
				"label": "Download",
				"color": confirm_download_button_color ?? "accent",
				"icon": {
					"show": true,
					"name": "feather:download",
					"flag": "downloadConfirmButtonIcon"
				}
			},
			"cancel": {
				"show": true,
				"label": "Cancel"
			}
		},
		"dismissible": true
		}
	}

	get moveForm () {
		const moveConfirmButtonColor = this.getConfirmElementsColor("MoveConfirmButton");
		return {
		"title": "Move",
		"message": "",
		"icon": {
			"show": true,
			"name": "feather:move",
			"color": "info"
		},
		"actions": {
			"confirm": {
				"show": true,
				"label": "Confirm",
				"color": moveConfirmButtonColor ?? 'warn'
			},
			"cancel": {
				"show": true,
				"label": "Cancel"
			}
		},
		"dismissible": true
		}
	}

	/**
	 * Represents the configuration for the restore form.
	 */
	get restoreForm() {
		const trashLocation = new LpmLabelPipe(this).transform('Trash', 'trashLocationLabel');
		return {
		"title": "Restore",
		"message": `You are about to move the item(s) from ${trashLocation} to their previous location path.<br><br>Proceed with restore?`,
		"icon": {
			"show": true,
			"name": "mat_outline:restore",
			"color": "info"
		},
		"actions": {
			"confirm": {
				"show": true,
				"label": "Restore",
				"color": "accent"
			},
			"cancel": {
				"show": true,
				"label": "Cancel"
			}
		},
		"dismissible": true
		};
	}

	/**
 * Represents the configuration for the delete label form.
 */
	public static deleteLabelForm = {
		"title": "Delete Label",
		"message": "Are you sure you want to delete this label? </br></br><span class=\"font-medium\">Doing so will remove this label from filter, search and tagging options!</span>",
		"icon": {
			"show": true,
			"name": "mat_outline:delete",
			"color": "error"
		},
		"actions": {
			"confirm": {
				"show": true,
				"label": "Delete Label",
				"color": "warn"
			},
			"cancel": {
				"show": true,
				"label": "Cancel"
			}
		},
		"dismissible": true
	}

	/**
	 * Represents the configuration for the End Copilot Session form
	 */
	public static endSessionForm = {
		"title": "End Session",
		"message": "You are about to leave Copilot mode and return to your previous view. The session data will not be saved. </br></br><span class=\"font-medium\">Are you sure?</span>",
		"icon": {
		"show": true,
		"name": "heroicons_outline:exclamation-triangle",
		"color": "warning"
		},
		"actions": {
			"confirm": {
				"show": true,
				"label": "End",
				"color": "warn"
			},
			"cancel": {
				"show": true,
				"label": "Cancel"
			}
		},
		"dismissible": true
	}

	logout(reason?: string, type?: string) {
		this.clearLocalStorage();
		this.router.navigate(['/apps/auth/sign-out']);

		// try {
		// 	if (reason != null) {
		// 		if (reason == "session-expire") {
		// 			this.router.navigateByUrl(`${url}?reason=${reason}`);
		// 		} else {
		// 			this.router.navigate([url], {
		// 				queryParams: {
		// 					reason: reason,
		// 					returnUrl: this.router.routerState.snapshot["url"],
		// 					queryParamsHandling: "merge",
		// 				},
		// 			});
		// 		}
		// 	} else {
		// 		this.router.navigateByUrl(url);
		// 	}
		// } catch (error) {
		// 	this.router.navigateByUrl(url);
		// }
	}

	public clearLocalStorage() {
		localStorage.removeItem(this.ACCOUNT_IN_SESSION);
		localStorage.removeItem(this.USER_TOKEN);
		localStorage.removeItem(this.USER_IN_SESSION);
		localStorage.removeItem(this.ROOT_IN_SESSION);
		localStorage.removeItem(this.SORT_PREFERNCES)
		localStorage.removeItem('WEB_APP');
		localStorage.removeItem('APP_SCOPE');
		localStorage.removeItem("pageLimit");
		localStorage.removeItem("pageLimitLarge");
	}

	public InvalidErrorResponse(ErrorMessage: string) {
		return ErrorMessage?.includes(`Expected ',' or '}' after property value`) ? true : false;
	}

	//basic empty,null or undefined check for variable
	public isValidated(value) {
		return value === null || value === undefined || value === "" ? false : true;
	}

	navigateToUserDetail(id) {
		this.router.navigateByUrl("/apps/settings/user-profile/" + id);
	}

	public navigateToPasswordReset(userId: string) {
		this.router.navigateByUrl("/apps/auth/account/reset/" + userId);
	}

	public navigateTo2FA(userId) {
		this.router.navigate(["/apps/auth/account/twofa"], {
			queryParams: {
				id: userId,
			},
		});
	}

	formatDate(date: Date): Date {
		return date
	}

	getDateRange(value): { startDate: Date, endDate: Date } {
		const selectedOption = value.toLowerCase()
		let startDate, endDate;

		switch (selectedOption) {
			case this.DateRanges.today:
				startDate = this.formatDate(startOfDay(new Date()));
				endDate = this.formatDate(endOfDay(new Date()));
				break;
			case this.DateRanges.yesterday:
				const yesterday = startOfDay(new Date());
				yesterday.setDate(yesterday.getDate() - 1);
				startDate = this.formatDate(startOfDay(yesterday));
				endDate = this.formatDate(endOfDay(yesterday));
				break;
			case this.DateRanges.last7:
				startDate = this.formatDate(startOfDay(new Date(new Date().getTime() - (7 * 24 * 60 * 60 * 1000))));
				endDate = this.formatDate(endOfDay(new Date()));
				break;
			case this.DateRanges.last30:
				startDate = this.formatDate(startOfMonth(new Date(new Date().getTime() - (30 * 24 * 60 * 60 * 1000))));
				endDate = this.formatDate(endOfDay(new Date()));
				break;
			case this.DateRanges.last90:
				startDate = this.formatDate(startOfQuarter(new Date(new Date().getTime() - (90 * 24 * 60 * 60 * 1000))));
				endDate = this.formatDate(endOfDay(new Date()));
				break;
			default:
				// For 'any' option or any other unrecognized value, return null or handle as needed
				startDate = null;
				endDate = null;
				break;
		}

		return { startDate, endDate };
	}

	getCondition(property, value) {
		switch (property) {
			case 'startDate':
				return `&condition=lastEditedAt,GTE,${value}`
			case 'endDate':
				return `&condition=lastEditedAt,LTE,${value}`
			case 'isFavourite':
				return `&condition=${property},EQ,${value}`
			case 'TRASHED':
				{
					return value == 'true' ? `&condition=status,LIKE,TRASHED` : "";
				}

			default:
				return `&condition=${property},LIKE,${value}`
		}
	}

	/**
	 * Get the file type image based on the folder type
	 *
	 * @param type The folder type
	 * @returns The file type image URL
	 */
	getFileTypeImage(type: string): string {
		// Mapping of non-standard file types to supported types
		const typeMapping: { [key: string]: string } = {
			'jpeg': 'jpg',
			'tif': 'tiff',
		};

		// Normalize the type and handle undefined or null values
		const normalizedType = type ? type.toLowerCase().replace(".", "") : null;

		// Use the mapping to rationalize the type if applicable
		const rationalizedType = typeMapping[normalizedType] || normalizedType;
		if (!rationalizedType) {
			return 'filespreviewfail';  // Return a default icon if type is null or empty
		}
	
		if (rationalizedType === 'folder') {
			return 'heroicons_outline:folder';  // Specific icon for folders
		}

		if (rationalizedType === 'file') {
			return 'heroicons_outline:document';  // Specific icon for folders
		}
		
		if (this.iconTypes.includes(rationalizedType)) {
			return rationalizedType;  // Return the icon type directly
		}
		
		return 'filespreviewfail';  // Standard fallback for unsupported types
	}

	/**
	 * Creates a drag image element for the given asset.
	 * 
	 * @param asset - The asset for which to create the drag image.
	 * @returns The drag image element.
	 */
	createDragImage(asset: Asset): HTMLElement {
		const dragGhost = document.createElement('div');
		dragGhost.className = 'drag-ghost';
		// Create the icon, here we use an 'img' element as a placeholder
		const icon = document.createElement('img');
		icon.className = 'file-icon icon-size-8';
		//const iconPath = `/assets/images/file-icons/${iconName}.svg`;
		//icon.src = `./assets/images/file-icons/doc.svg`;
		const iconType = asset.type === 'FOLDER' ? 'folder' : asset.extension;
		//const extension = this.getFileTypeImage(iconType);
		icon.src = `./assets/images/file-icons/${this.getFileTypeImage(iconType)}.svg`; // Path to your SVG icon
		//icon.src = `./assets/images/file-icons/doc.svg`;
		dragGhost.appendChild(icon);

		// Create the title div with the file name
		const title = document.createElement('div');
		title.className = 'drag-ghost-title';
		title.textContent = asset.name; // Set file name
		dragGhost.appendChild(title);
		dragGhost.style.opacity = '1.0';
		// Style the ghost container to be hidden from the viewport initially
		dragGhost.style.position = 'absolute';
		dragGhost.style.top = '-99999px';

		return dragGhost;
	}

	/**
	 * Handles the drag start event for an asset row: builds a floating drag-ghost
	 * image, attaches it to the drag event, then removes it on the next tick.
	 */
	onDragStart(event: DragEvent, asset: Asset): void {
		const img = this.createDragImage(asset);
		document.body.appendChild(img);
		event.dataTransfer.setDragImage(img, 0, 0);
		setTimeout(() => document.body.removeChild(img), 0);
	}

	/**
	 * Checks whether `id` is present in `list` (by `.id`).
	 */
	selectedLabel(id: string, list: any[]) {
		if (list) {
			const index = list.findIndex(item => item.id === id);
			return index == -1 ? false : true;
		}
	}

	/**
	 * Filters `labels` down to the ones present in `assignedLabelIds`.
	 */
	getAssetLabels(labels: any[], assignedLabelIds: string) {
		return labels.filter(label => assignedLabelIds?.includes(label.id));
	}

	public getUserColor(username: String) {
		const clientTheme = this.getWebComponentInputParams().clientTheme;
		const customThemeColorClass = CUSTOM_THEME_ELEMENTS_CLASSES[clientTheme.name]?.user_bubble;
		if(customThemeColorClass){
			return customThemeColorClass;
		}
		else{
		let index = 0;
		let alphabet = "bg-green-700 hover:bg-green-800";

		let letter = username != null && username.length > 1 ? username.charAt(0) : "z";
		index = alphabet.indexOf(letter.toLowerCase());
		let coloredCircle = 'bg-orange-700 hover:bg-orange-800';
		switch (true) {
			case index >= 0 && index < 3: //abc
				coloredCircle = "bg-orange-700 hover:bg-orange-800"; // Red
				break;
			case index >= 3 && index < 7: //def
				coloredCircle = "bg-red-700 hover:bg-red-800";
				break;
			case index >= 7 && index < 10: //ghi
				coloredCircle = "bg-pink-700 hover:bg-pink-800";
				break;
			case index >= 10 && index < 13: //jkl
				coloredCircle = "bg-green-700 hover:bg-green-800";
				break;
			case index >= 13 && index < 16: //mno
				coloredCircle = "bg-teal-700 hover:bg-teal-800";
				break;
			case index >= 16 && index < 19: //pqr
				coloredCircle = "bg-cyan-700 hover:bg-cyan-800";
				break;
			case index >= 19 && index < 21: //stu
				coloredCircle = "bg-indigo-700 hover:bg-indigo-800";
				break;
			case index >= 21 && index < 24: //vwx
				coloredCircle = "bg-blue-700 hover:bg-blue-800";
				break;
			case index >= 24 && index < 26: //yx
				coloredCircle = "bg-purple-700 hover:bg-purple-800";
				break;
		}
		// tslint:disable-next-line: align
		return coloredCircle + ' user_bubble ';
		}
	}

	public getLabelCol(color: String) {
		let index = 0;
		let alphabet = "abcdefghijklmnopqrstuvqxyz";

		let letter = color != null && color.length > 1 ? color.charAt(0) : "z";
		index = alphabet.indexOf(letter.toLowerCase());
		let coloredCircle;
		switch (true) {
			case index >= 0 && index < 3: //abc
				coloredCircle = "text-orange-800 bg-orange-100"; // Red
				break;
			case index >= 3 && index < 7: //def
				coloredCircle = "text-red-800 bg-red-100";
				break;
			case index >= 7 && index < 10: //ghi
				coloredCircle = "text-pink-800 bg-pink-100";
				break;
			case index >= 10 && index < 13: //jkl
				coloredCircle = "text-green-800 bg-green-100";
				break;
			case index >= 13 && index < 16: //mno
				coloredCircle = "text-teal-800 bg-teal-100";
				break;
			case index >= 16 && index < 19: //pqr
				coloredCircle = "text-cyan-800 bg-cyan-100";
				break;
			case index >= 19 && index < 21: //stu
				coloredCircle = "text-indigo-800 bg-indigo-100";
				break;
			case index >= 21 && index < 24: //vwx
				coloredCircle = "text-blue-800 bg-blue-100";
				break;
			case index >= 24 && index < 26: //yx
				coloredCircle = "text-purple-800 bg-purple-100";
				break;
		}
		// tslint:disable-next-line: align
		return coloredCircle;
	}

	getDescription(type:activity) {
		let activity = new ActivityDes();
		return type.actionType=='ITEM_MOVED' ? type.actionOutcome : activity[type.actionType];
	}

	public navigateToConfirmEmail(id) {
		this.router.navigateByUrl("/apps/auth/signup/confirm/" + id);
	}

	getEditorToolbarOptions(){
		let _this = this;
		const toolbarOption = {
			toolbar: false,
				// ['bold', 'italic', 'underline'],
				// [{align: []}, {list: 'ordered'}, {list: 'bullet'}],
				//['clean'],

			mention: {
				allowedChars: /^[A-Za-z\sÅÄÖåäö]*$/,
				mentionDenotationChars: ["@"],
				source: (searchTerm, renderList, mentionChar) => {
					let values;

					if (mentionChar === "@") {
						values = this.mentionedUsers;
					}

					if (searchTerm.length === 0) {
						renderList(values, searchTerm);
					} else {
						const matches = [];
						for (var i = 0; i < values.length; i++)
							if (~values[i].value.toLowerCase().indexOf(searchTerm.toLowerCase())) matches.push(values[i]);
						renderList(matches, searchTerm);
					}
				},
			},
		};
		return toolbarOption;
	}

	/**
	 * Checks if the given asset is locked by the logged-in user.
	 * @param asset - The asset to check.
	 * @returns A boolean indicating whether the asset is locked by the logged-in user.
	 */
	public isLockedByLoggedInUser(asset: Asset): boolean {
		//let loggedInUser: UserImpl = this.getUserFromSession();
		const lockedByLoggedIn : boolean = asset?.lockDetail?.userId === this.getUserId() ? true : false;
		return lockedByLoggedIn;
	}

	/**
	 * Checks if the asset is locked AND if it was locked by the current user.
	 * @param asset - The asset to check.
	 * @returns A boolean value indicating whether the asset is locked for the current user.
	 */
	public isLockedForCurrentUser(asset:Asset){
        let isLocked = asset?.locked == null ? false : asset.locked;
        let isLockedByLoggedInUser = this.isLockedByLoggedInUser(asset);
        return isLocked && !isLockedByLoggedInUser;
    }

	/**
	 * Checks if an asset is checked out for the current user.
	 * An asset is considered 'checked out' if it is locked AND is locked for the current user.
	 * @param asset - The asset to check.
	 * @returns A boolean value indicating whether the asset is checked out.
	 */
	public isCheckedOut(asset:Asset){
        return asset.locked && !this.isLockedForCurrentUser(asset)
    }

	/**
	 * Checks if an asset is requestable. 
	 * An asset is considered 'requestable' if it is locked AND was NOT locked by the current user.
	 * @param asset - The asset to check.
	 * @returns A boolean indicating whether the asset is requestable.
	 */
    public isRequestable(asset: Asset): boolean {
        return asset.locked && this.isLockedForCurrentUser(asset)
    }

	/**
	 * Checks if an asset is checked in (i.e. not locked).
	 * @param asset - The asset to check.
	 * @returns A boolean indicating whether the asset is checked in.
	 */
    public isCheckedIn(asset: Asset): boolean {
        return !asset.locked
    }

	public getSortDirection(direction:SortDirection){
		if(direction=='asc') return 'ASCENDING'
		if(direction=='desc') return 'DESCENDING'
		return null
	}

	getSortingPreference(viewName: DMSViews): SortingPreference | null {
		const preferencesStr = localStorage.getItem(this.SORT_PREFERNCES);
		if (preferencesStr) {
		  const preferences: SortingPreference[] = JSON.parse(preferencesStr);
		  return preferences.find(pref => pref.viewName === viewName) || null;
		}
		return null;
	  }
	
	  setSortingPreference(preference: SortingPreference) {
		let preferences: SortingPreference[] = [];
		const preferencesStr = localStorage.getItem(this.SORT_PREFERNCES);
		if (preferencesStr) {
		  preferences = JSON.parse(preferencesStr);
		}
		const existingIndex = preferences.findIndex(pref => pref.viewName === preference.viewName);
		if (existingIndex !== -1) {
		  preferences[existingIndex] = preference;
		} else {
		  preferences.push(preference);
		}
		localStorage.setItem(this.SORT_PREFERNCES, JSON.stringify(preferences));
	  }

	  getDmsViewerUrl():string{
			return environment.DMS_VIEWER
	  }

	  getDmsEditorUrl():string{
			return environment.DMS_EDITOR
	  }

	  /** Returns per-OS download URLs for the DMS Desktop Agent */
	  getDeskAppDownloadUrls(): { linux: string; windows: string; mac: string } {
		return {
		  linux:   environment.DMS_DESK_APP_LINUX,
		  windows: environment.DMS_DESK_APP_WINDOWS,
		  mac:     environment.DMS_DESK_APP_MAC,
		};
	  }

	  /**
	   * Opens drive://<assetId> without navigating the current page.
	   * Uses a hidden, programmatically-clicked anchor rather than window.open
	   * (which leaves a throwaway tab — closing that tab, even after a delay,
	   * can tear down Safari's native "Open in App?" sheet before the user
	   * gets to answer it) or location.href (Safari shows "cannot open the
	   * page because the address is invalid" instead of prompting).
	   * Must be called in the original click handler: Safari ignores custom
	   * protocols once the call is no longer part of the user gesture.
	   */
	  launchDesktopAgentDeepLink(assetId: string): void {
		const deepLink = `drive://${assetId}`;
		const anchor = document.createElement('a');
		anchor.href = deepLink;
		anchor.style.display = 'none';
		document.body.appendChild(anchor);
		anchor.click();
		anchor.remove();
	  }

	  /** True for Safari (desktop or mobile), false for Chrome/Edge/Firefox/etc, even though they also match "safari" in their UA string. */
	  public isSafariBrowser(): boolean {
		const ua = navigator.userAgent;
		return /^((?!chrome|android|crios|fxios|edg).)*safari/i.test(ua);
	  }

	  formatBytes(bytes, decimals?):string {
		if (bytes == 0) return '0 Bytes';
		const k = 1024,
		  dm = decimals || 2,
		  sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'],
		  i = Math.floor(Math.log(bytes) / Math.log(k));
		return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
	  }

	  pluralize(string:string,length:number){
		const strings={
			item:"items",
			file:"files",
			folder:"folders",
		}
		if(length>1){
			return strings[string]??string+"s"
		}
		else
		{
			return string
		}
	  }

	  getMarkedContent(markdown: string,details=[]): SafeHtml {
		const renderer = new marked.Renderer();

		// Replace citations in plain text
		  // Replace citations using details array index
		  const replaceCitations = (str: string) => {
			return str.replace(/\[ID:(\d+)[^\]]*\]/g, (match, id) => {
			  const index = Number(id);
			  const detail = details[index];
			  if (!detail) return match;
		  
			  // Truncate documentName to max 30 characters
			  const truncatedName =
				detail.documentName.length > 30
				  ? detail.documentName.slice(0, 30) + "…"
				  : detail.documentName;
		  
			  const titleText = `${truncatedName} Page: ${detail.pageNumbers}`;
		  
			  return `<span class="inline-flex items-center cursor-pointer text-blue-500 hover:underline"
						  data-ref-index="${index}" 
						  title="${titleText}">
						📄<sup>[${index}]</sup>
					  </span>`;
			});
		  };
		  
		  renderer.text = (token: any) => {
			  const str = typeof token === 'string' ? token : token.text || '';
			  return replaceCitations(str);
		  };
	  
		// 2️⃣ Custom table renderer (yours)
		renderer.table = (token) => {
		  const header = token.header.map(cell =>
			`<th class="px-4 py-2 border-b text-left text-sm font-semibold
			   dark:bg-gray-800 dark:text-white bg-gray-100 text-gray-800
			">${cell.text}</th>`
		  ).join('');
	  
		  const body = token.rows.map(row =>
			
			`<tr class="dark:hover:bg-gray-700 hover:bg-gray-50">
			  ${row.map(cell =>
				`<td class="px-4 py-2 border-b text-sm dark:text-white text-gray-700">${replaceCitations(cell.text)}</td>`
			  ).join('')}
			</tr>`
		  ).join('');
	  
		  return `
			<div class="overflow-x-auto">
			  <table class="min-w-full border dark:border-gray-600 border-gray-300 rounded-lg">
				<thead><tr>${header}</tr></thead>
				<tbody>${body}</tbody>
			  </table>
			</div>
		  `;
		};

		// Helper: padding for nesting
		const getIndentClass = (depth: number) => {
			const map: Record<number, string> = {
			  0: 'pl-3',
			  1: 'pl-4',
			  2: 'pl-5',
			  3: 'pl-6',
			  4: 'pl-7'
			};
			return map[depth] || 'pl-16';
		  };
		
		  // 3️⃣ Nested list renderer
		  const renderList = (token: any, depth: number): string => {
			const ordered = token.ordered;
			const body = token.items
			  .map((item: any) => renderListItem(item, depth))
			  .join('');
		
			const tag = ordered ? 'ol' : 'ul';
			const listClass = ordered
			  ? 'dark:list-decimal dark:text-white list-decimal'
			  : 'dark:list-disc list-disc';
		
			return `<${tag} class="${listClass} ${getIndentClass(depth)} space-y-1 text-sm">${body}</${tag}>`;
		  };
		
		  const renderListItem = (token: any, depth: number): string => {
			let content = '';
		  
			// If token has child tokens, process them recursively
			if (token.tokens && token.tokens.length > 0) {
			  content = token.tokens.map((t: any) => {
				if (t.type === 'text') return replaceCitations(t.text);
				if (t.type === 'list') return renderList(t, depth + 1);
				return t.raw || '';
			  }).join('');
			} else {
			  // Fallback: just use token.text
			  content = replaceCitations(token.text || '');
			}
		  
			const sizeClass = 'text-base';
			return `<li class="leading-relaxed ${sizeClass}">${content}</li>`;
		  };
		
		  renderer.list = (token) => renderList(token, 0);
		  renderer.listitem = (token) => renderListItem(token, 0);
	  
		// 3️⃣ Parse markdown
		marked.setOptions({ renderer });
		const parsed = marked.parse(markdown, { async: false }) as string;
	  
		return this.sanitizer.bypassSecurityTrustHtml(parsed);
	  }

	  getFileIcon(fileName: string): string {
        const ext = fileName.split('.').pop()?.toLowerCase();
        switch (ext) {
          case 'pdf':
            return 'picture_as_pdf';
          case 'doc':
          case 'docx':
            return 'description';
          case 'xls':
          case 'xlsx':
            return 'table_chart';
          case 'png':
          case 'jpg':
          case 'jpeg':
          case 'gif':
            return 'image';
          case 'zip':
          case 'rar':
            return 'folder_zip';
          case 'txt':
            return 'notes';
          default:
            return 'insert_drive_file';
        }
      }

	  public getQueryMethod(queryParams: Object) {
		const queryString = Object.entries(queryParams)
		  .filter(([key, value]) => value != null && value != '')
		  .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
		  .join("&");
		  if(queryString.length==0){
			return '';
		  }
		const method = `?${queryString}`;
		return method;
	  }

	  getFileUploadOptions(folderId:string, lpm:LpmConfig):FileUploadOptions{
		const fileUploadOptions:FileUploadOptions = {
            folderId: folderId,
            contactId: lpm.copilotConfig?.contactIds.join(',') ?? null,
            matterId : lpm.copilotConfig?.matterId ?? null
        }
		return fileUploadOptions
	  }

	/**
	 * Splits a flat file list into those within the size limit and those exceeding it.
	 * @param files - The files to check (e.g. from a file input's change event).
	 */
	filterOversizedFiles(files: File[]): { files: File[]; oversizedFiles: OversizedFile[] } {
		const allowedFiles: File[] = [];
		const oversizedFiles: OversizedFile[] = [];
		for (const file of files) {
			if (file.size > DMS_MAX_FILE_SIZE_BYTES) {
				oversizedFiles.push({ name: file.name, size: this.formatBytes(file.size, 1), path: '' });
			} else {
				allowedFiles.push(file);
			}
		}
		return { files: allowedFiles, oversizedFiles };
	}

	buildHierarchyFromFiles(files: FileList): { hierarchy: pickerDirectory[]; oversizedFiles: OversizedFile[] } {
		const oversizedFiles: OversizedFile[] = [];
		const root: pickerDirectory[] = [];
		const folderMap = new Map<string, pickerDirectory>();

		Array.from(files).forEach(file => {
			const parts = file.webkitRelativePath.split('/');

			// Process all parts starting from index 1 (skip root folder name)
			for (let i = 1; i < parts.length; i++) {
				const part = parts[i];
				const currentPath = parts.slice(1, i + 1).join('/'); // Skip root in path
				const parentPath = i > 1 ? parts.slice(1, i).join('/') : '';

				// If this is a file (last part)
				if (i === parts.length - 1) {
					// Check file size
					if (file.size < DMS_MAX_FILE_SIZE_BYTES) {
						const fileItem: pickerDirectory = {
							type: 'file',
							name: part,
							file: file
						};

						if (parentPath) {
							const parent = folderMap.get(parentPath);
							if (parent) {
								parent.children.push(fileItem);
							}
						} else {
							// File is directly in root
							root.push(fileItem);
						}
					} else {
						// File exceeds size limit
						const MB: string = this.formatBytes(file.size, 1);
						const oversizedFile = {
							name: file.name,
							size: MB,
							path: currentPath
						};
						oversizedFiles.push(oversizedFile);
					}
				}
				// If this is a folder (not the last part)
				else {
					if (!folderMap.has(currentPath)) {
						const folderItem: pickerDirectory = {
							type: 'directory',
							name: part,
							children: []
						};

						folderMap.set(currentPath, folderItem);

						if (parentPath) {
							const parent = folderMap.get(parentPath);
							if (parent) {
								parent.children.push(folderItem);
							}
						} else {
							// Folder is directly in root
							root.push(folderItem);
						}
					}
				}
			}
		});

		return { hierarchy: root, oversizedFiles: oversizedFiles };
	}

	/**
		 * Recursively traverses a directory and returns an array of directory entries.
		 * Each entry contains the name, type, and children (if it's a directory) or file (if it's a file).
		 *
		 * @param directoryHandle - The handle of the directory to traverse.
		 * @returns An array of directory entries.
		 */
	// `directoryHandle` is a FileSystemDirectoryHandle (File System Access API); left as `any`
	// since those DOM types aren't part of this project's configured `lib`.
	async traverseDirectory(directoryHandle: any, currentPath: string = ""): Promise<{ hierarchy: pickerDirectory[]; oversizedFiles: OversizedFile[] }> {
		const oversizedFiles: OversizedFile[] = [];
		const entries: pickerDirectory[] = [];

		for await (const [name, handle] of directoryHandle) {
			const newPath = currentPath ? `${currentPath}/${name}` : name;
			if (handle.kind === 'directory') {
				const subHierarchy = (await this.traverseDirectory(handle, newPath)).hierarchy;
				entries.push({ name, type: 'directory', children: subHierarchy });
			} else {
				const file: File = await handle.getFile()
				if (file.size < DMS_MAX_FILE_SIZE_BYTES) {
					entries.push({ name, type: 'file', file });
				}
				else {
					const MB: string = this.formatBytes(file.size, 1)
					const oversizedFile: OversizedFile = { name: file.name, size: MB, path: newPath }
					oversizedFiles.push(oversizedFile)
				}

			}
		}
		return { hierarchy: entries, oversizedFiles: oversizedFiles };
	}

	/**
	 * Traverses (from native drag-and-drop) and returns the same
	 * hierarchy shape as traverseDirectory, so folder-drops can reuse the same upload path.
	 */
	async traverseDroppedDirectory(entry: FileSystemDirectoryEntry, currentPath = ''): Promise<{ hierarchy: pickerDirectory[]; oversizedFiles: OversizedFile[] }> {
		const oversizedFiles: OversizedFile[] = [];
		const entries: pickerDirectory[] = [];

		const reader = entry.createReader();
		const rawEntries = await this._readAllDropEntries(reader);

		for (const childEntry of rawEntries) {
			const newPath = currentPath ? `${currentPath}/${childEntry.name}` : childEntry.name;
			if (childEntry.isDirectory) {
				const sub = await this.traverseDroppedDirectory(childEntry as FileSystemDirectoryEntry, newPath);
				entries.push({ name: childEntry.name, type: 'directory', children: sub.hierarchy });
				oversizedFiles.push(...sub.oversizedFiles);
			} else {
				const file = await this.getFileFromDroppedEntry(childEntry as FileSystemFileEntry);
				if (file.size < DMS_MAX_FILE_SIZE_BYTES) {
					entries.push({ name: childEntry.name, type: 'file', file });
				} else {
					oversizedFiles.push({ name: file.name, size: this.formatBytes(file.size, 1), path: newPath });
				}
			}
		}

		return { hierarchy: entries, oversizedFiles };
	}

	private _readAllDropEntries(reader: FileSystemDirectoryReader): Promise<FileSystemEntry[]> {
		return new Promise((resolve, reject) => {
			const results: FileSystemEntry[] = [];
			const readBatch = () => {
				reader.readEntries(batch => {
					if (batch.length === 0) { resolve(results); return; }
					results.push(...batch);
					readBatch();
				}, reject);
			};
			readBatch();
		});
	}

	getFileFromDroppedEntry(entry: FileSystemFileEntry): Promise<File> {
		return new Promise((resolve, reject) => entry.file(resolve, reject));
	}

	shouldIncludeParentFolderInUpload(folderName: string, lpm:LpmConfig): Promise<boolean> {
		return new Promise((resolve) => {
			if (lpm?.isSupportUser) {
				const message =
					`You selected the folder <strong>"${folderName}"</strong>.<br><br>` +
					`Would you like to include this folder in the upload?<br><br>` +
					`Click <strong>Upload Folder</strong> to upload the folder itself with its contents.<br>` +
					`Click <strong>Upload Only Content</strong> to upload only the contents inside the folder.`;

				const dialogRef = this._fuseConfirmationService.open({
					message,
					icon: {
						show: true,
						name: 'feather:info',
						color: 'primary',
					},
					actions: {
						confirm: {
							label: 'Upload Folder',
							color: 'primary',
						},
						cancel: {
							label: 'Upload Only Content',
						},
					},
				});

				dialogRef.afterClosed().subscribe((result) => {
					const isParentIncluded = result === 'confirmed';
					resolve(isParentIncluded);
				});
				return;
			}
			// Normal users → always include folder
			resolve(true);
		});
	}

	openExternalViewEditLink(url: string) {
		// return; // bypassing the function due to server side rendering library issue.
		window.open(url, '_blank');
	}

	/**
	 * Opens the file in DMS Viewer.
	 * @param asset The file to view.
	 */
	public async viewFile(asset: Asset | CopilotAssetDetails): Promise<void> {
		if (!this._injector.get(PermissionsService).canView(asset as Asset)) {
			this.showNotViewableNotice(asset as Asset);
			return;
		}
		const assetId = asset.id;
		const assetName = encodeURIComponent(asset.name);
		const fileExt = encodeURIComponent(asset.extension);
		const userType = this.getLpmUserTypeParamForViewer();
		const dmsViewerUrl = this.getDmsViewerUrl();
		const authService = this._injector.get(AuthService);

		return authService.getToken()
			.then((res) => {
				const token = authService.getData(res).token;
				const themeName = encodeURIComponent(this.WebComponentInputParams?.clientTheme?.name ?? 'default');
				const url = `${dmsViewerUrl}/#/auth/${assetId}/?name=${assetName}&t=${token}&dut=${userType}&file_ext=${fileExt}&theme=${themeName}`;
				this.openExternalViewEditLink(url);
			})
			.catch((err) => {
				console.error(err);
			});
	}

	/**
	 * Non-viewable files: open on desktop directly, else offer Download in a modal.
	 * @param asset The file that can't be previewed.
	 */
	private showNotViewableNotice(asset: Asset): void {
		// Desktop first, no prompt
		if (asset?.type === 'FILE' && this._injector.get(ActionRegistryService).isAllowed('openOnDesktop', asset)) {
			this._injector.get(DesktopAgentService).launch(asset);
			return;
		}

		// Download only unlocked files
		const canDownload = asset?.type === 'FILE' && !this.isLockedForCurrentUser(asset);
		const message = canDownload
			? "This file type can't be opened in the browser.<br><br>You can download it and open it on your device instead."
			: "This file type can't be opened in the browser.";

		const dialogRef = this._fuseConfirmationService.open({
			title: 'Preview not available',
			message,
			icon: {
				show: true,
				name: 'feather:info',
				color: 'info',
			},
			actions: {
				confirm: {
					show: canDownload,
					label: 'Download',
					color: 'primary',
				},
				cancel: {
					show: true,
					label: canDownload ? 'Cancel' : 'OK',
				},
			},
			dismissible: true,
		});

		dialogRef.afterClosed().subscribe((result) => {
			if (result === 'confirmed') {
				this._injector.get(FileManagerService).downloadFile(asset);
			}
		});
	}

	onThumbnailClick(asset: Asset) {
		this.viewFile(asset);
        // if(asset.thumbnail){
        //     window.open(asset.thumbnail, '_blank');
        // }
      }
	
	getLpmUserTypeParamForViewer():string{
			const lpmScope = this.USER_TYPE;
			if(!this.getIsWebApp()) return 'D'; // default to 'D' for non-webapp contexts
			const user_type = consts.USER_TYPE_QUERY_MAPPING[lpmScope] ?? 'D';
			return user_type;
	}

	getConfirmElementsColor(label: string): string {
		const theme_name = this.WebComponentInputParams.clientTheme?.name ?? 'default';
		const theme_colors = {
			panther_default: {
				"Download": "primary",
				"TrashConfirmButton":'warn',
				"MoveConfirmButton":'warn',
				"DownloadIcon":'primary',
				"dialogIcon":'primary'
			},
			default: {
				"Download": "accent",
				"MoveConfirmButton":'primary',
				"DownloadIcon":'info'
			}
		}

		return theme_colors[theme_name]?.[label] ?? null;
	}

}
